<template>
  <div class="privacy-policy-page">
    <!-- PAGE HERO -->
    <DeliveryHero
      :title="heading"
      subtitle="How we collect, use, share, and protect your information when you shop with us."
    />

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <DeliverySidebar
        ref="sidebar"
        :sections="sections"
        :active-id="activeId"
        @navigate="scrollToSection"
      />

      <!-- Content -->
      <div class="policy-content">
        <!-- Intro -->
        <p class="policy-intro reveal">
          <strong>Effective date: {{ effectiveDate }}</strong>
          <br />
          Thank you for visiting hooliganseyewear.com, owned and operated by
          Hooligans Eyewear Inc. We have created this policy to provide you with
          information about how we collect, use, share, and protect information
          from our sites, mobile applications, and social media pages when you
          shop with us or otherwise interact with us.
        </p>

        <!-- ── Information Collected ─────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="information-collected"
          eyebrow="Data Collection"
          title="Information Collected"
        >
          <p>
            This section describes the information we collect, including
            information that you provide to us, information automatically
            collected, and information from third parties. We may combine
            information that we collect via one method with information
            collected via another to get a more complete view of our consumers —
            allowing us to serve you better and with more customization.
          </p>

          <h3>Information you provide</h3>
          <p>
            If you register at a site, make purchases, join a loyalty program,
            enter a contest or promotion, submit a survey, photo, video or
            product review, or sign up to receive emails, we collect and store
            the information provided. This includes personal information such as
            your name, social media handle, email, telephone number, home
            address, and payment information (such as account or credit card
            number). You may also be asked for demographic information (such as
            age, product preferences, zip code) or location information.
          </p>

          <CalloutBox>
            <strong>Social media login:</strong> You may register and log in
            using a social media account such as Facebook. When you do, Facebook
            will ask your permission to share certain information — including
            your name, gender, profile picture, friends list, and likes — which
            is provided to us under Facebook's Privacy Policy. You can control
            what we receive via your Facebook privacy settings.
          </CalloutBox>
        </PolicySection>

        <!-- ── Automatically Collected ───────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="automatically-collected"
          eyebrow="Passive Data"
          title="Automatically Collected Information"
        >
          <h3>Mobile services &amp; apps</h3>
          <p>
            Certain of our mobile apps offer opt-in geo-location services and
            push notifications. Geo-location services provide location-based
            content such as store locators, local weather, promotional offers,
            and personalized content. Push notifications may include discounts,
            reminders, or details about local events. Most mobile devices allow
            you to turn these off at any time. If you consent to location
            services, we will collect information about nearby Wi-Fi routers and
            cell towers to provide location-based content.
          </p>

          <h3>Site usage information</h3>
          <p>
            Hooligans Eyewear Inc and its third-party service providers use
            technologies to passively collect information about how our sites
            are accessed and used, including your browser type, device type,
            operating system, application version, pages served, time browsing,
            preceding page views, and your use of features or applications on
            the sites.
          </p>

          <h3>Device identifiers</h3>
          <p>
            We automatically collect an IP address or other unique identifier
            for the computer, mobile device, or other technology you use to
            access the sites. A device identifier is a number automatically
            assigned to your device when you access a website. We may use device
            identifiers to administer the sites, diagnose server problems,
            analyze trends, track page movements, identify your shopping cart,
            deliver advertising, and gather broad demographic information.
          </p>

          <h3>Cookies</h3>
          <p>
            Like many websites, we use cookies — data files placed on your
            browser when you visit the sites — to facilitate navigation and
            personalize your experience, including tailoring advertisements.
            Cookies may also associate you with social networking sites like
            Facebook and Twitter and deliver targeted ads. You can control
            cookies on your device, but disabling them may limit your ability to
            use certain features.
          </p>

          <h3>Pixels &amp; web beacons</h3>
          <p>
            We use pixel tags and web beacons — transparent graphic images
            placed on a web page or in an email — to indicate that a page or
            email has been viewed, or to request content from another server.
            These allow us to measure the performance of our email campaigns and
            improve our site by identifying which content is effective.
          </p>
        </PolicySection>

        <!-- ── Product Analytics (PostHog) ───────────────────── -->
        <PolicySection
          class="reveal"
          section-id="product-analytics"
          eyebrow="Analytics"
          title="Product Analytics (PostHog)"
        >
          <p>
            Analytics is on by default, and you can turn it off at any time in
            “Your privacy choices” in the footer. When Analytics is on, we use
            PostHog, Inc. as our service provider (processor) to understand how
            visitors use our store and to improve it.
          </p>

          <h3>What PostHog receives</h3>
          <p>
            A pseudonymous visitor ID and session ID, stored in a first-party
            cookie and in your browser's local storage; the pages you view, with
            sensitive web-address parameters removed; product, cart, and
            checkout events, such as product identifiers, prices, quantities,
            and order totals, but never payment card details or order numbers;
            your device, browser, operating system, and referring site; and your
            IP address, which PostHog uses only to estimate your country and
            region and then discards.
          </p>

          <h3>If you sign in or check out</h3>
          <p>
            If you sign in to your account, we send PostHog an internal customer
            identifier and the email address on your account. That connects your
            visits to your account, including earlier visits on that browser
            before you signed in. If you check out without an account while
            Analytics is on, we also send PostHog the email address you gave for
            the order, so that purchase is connected to that browser's earlier
            and later visits. We never send PostHog your name, phone number, or
            postal address.
          </p>

          <h3>How PostHog handles this information</h3>
          <p>
            PostHog may use this information only to provide its service to us
            and may not sell or share it. We have opted out of PostHog using our
            data to train its models. The data is stored in the United States
            (PostHog US Cloud, hosted on Amazon Web Services), and PostHog's
            subprocessors are listed at
            <a
              href="https://posthog.com/subprocessors"
              target="_blank"
              rel="noopener noreferrer"
              >posthog.com/subprocessors</a
            >. We keep analytics events for up to 12 months.
          </p>

          <CalloutBox>
            <strong>Your choices:</strong> Turning Analytics off in “Your
            privacy choices” stops PostHog on that browser, and an enabled
            Global Privacy Control signal also turns PostHog off on that
            browser. To request deletion of analytics data connected to your
            customer account or to the email address you gave for an order,
            email
            <a href="mailto:support@hooliganseyewear.com"
              >support@hooliganseyewear.com</a
            >.
          </CalloutBox>
        </PolicySection>

        <!-- ── Sharing Your Information ──────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="sharing"
          eyebrow="Third Parties"
          title="Sharing Your Information"
        >
          <h3>Third parties providing services on our behalf</h3>
          <p>
            We share your personal information with third parties that perform
            functions on our behalf, such as service providers that host or
            operate our sites, provide analytics, process transactions and
            payments, fulfill orders, provide customer service, administer
            loyalty programs, and assist with promotional activities. We may
            also share information with subsidiaries, sponsors, partners,
            advertisers, and other third parties for product information,
            promotional offers, and business operations.
          </p>

          <h3>Google Customer Reviews</h3>
          <p>
            After a completed purchase, we share your order number, email
            address, delivery country, and estimated delivery date with Google
            to display an invitation to Google Customer Reviews. Participation
            is voluntary. Google sends a survey email only if you agree in that
            invitation. Google's handling of this information is described in
            its
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              >Privacy Policy</a
            >.
          </p>

          <h3>Your agreement to share</h3>
          <p>
            While on our sites, you may have the opportunity to opt in to
            receive information from third parties or consent to sharing your
            information with them, including social networks like Facebook or
            Twitter. If you agree to share your personal information with a
            third party, that information will be subject to the privacy policy
            and business practices of that third party.
          </p>
          <p>
            If you connect your Facebook account to your Hooligans Eyewear
            account, your Facebook username and profile picture may be made
            available to other users of the site. Your shopping activity may be
            automatically shared with your Facebook friends. Information shared
            to Facebook will be governed by Facebook's Privacy Policy. If you do
            not wish for such sharing to take place, do not agree to share your
            activity to Facebook and/or adjust your settings accordingly.
          </p>

          <h3>Business transfers</h3>
          <p>
            We may share information with other entities and affiliates of
            Hooligans Eyewear Inc. If another entity acquires us or our assets,
            personal information and usage information may be disclosed to such
            entity as one of the transferred assets. In the event of bankruptcy
            or reorganization, all such information may be considered an asset
            and sold or transferred to third parties.
          </p>

          <h3>Legal disclosure</h3>
          <p>
            Hooligans Eyewear Inc may transfer and disclose personal
            information, usage information, and device identifiers to third
            parties to comply with a legal obligation; when we believe in good
            faith that the law requires it; at the request of governmental
            authorities; to verify or enforce our Terms of Use or other
            applicable policies; to respond to an emergency; or to protect the
            rights, property, safety, or security of third parties, visitors, or
            the public.
          </p>
        </PolicySection>

        <!-- ── Advertising & Opt-Out ─────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="advertising"
          eyebrow="Marketing"
          title="Advertising &amp; Opt-Out"
        >
          <p>
            Hooligans Eyewear Inc licenses technology to serve advertisements on
            its own sites and across the internet. We may also use third parties
            to serve advertisements, measure site performance, and provide
            analytics. These providers may set and access their own cookies,
            pixel tags, and similar technologies on your device and may collect
            information about you, including usage information.
          </p>
          <p>
            We and our network advertisers may target advertisements for
            products and services in which you might be interested based on your
            visits to our sites and other websites, or based on location data
            from your device. Other parties, such as Google, Meta, and
            Pinterest, may collect information about your online activities over
            time and across different websites when Advertising is enabled in
            “Your privacy choices”.
          </p>

          <h3>Do Not Track and Global Privacy Control</h3>
          <p>
            We do not treat the legacy Do Not Track signal as a privacy choice,
            because browsers and websites do not apply it consistently. We do
            honor Global Privacy Control (GPC): when your browser sends an
            enabled GPC signal, we treat it as an opt-out of advertising, sale,
            and sharing on that browser or device, and we turn off PostHog
            product analytics on that browser. You can also turn Analytics and
            Advertising off at any time in “Your privacy choices” in the footer.
            Your choice is saved in your browser and in a first-party cookie,
            vf_privacy, for up to 400 days, renewed when you visit.
          </p>

          <CalloutBox>
            <strong>How to opt out:</strong> If you prefer not to receive
            targeted advertising, you can opt out of certain network advertising
            programs. Options are device-specific, so you may need to opt out on
            each device you use. You can also opt out by clicking the AdChoices
            icon on some of our advertising. Note that even after opting out,
            you will still see advertisements — they may simply be less relevant
            to your interests.
          </CalloutBox>
        </PolicySection>

        <!-- ── Children ──────────────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="children"
          eyebrow="Age &amp; Minors"
          title="Children"
        >
          <p>
            Our sites are not directed to children under 13. We do not knowingly
            collect, use, or disclose personally identifiable information from
            anyone under 13 years of age. If we determine upon collection that a
            user is under this age, we will not use or maintain their personal
            information without the parent or guardian's consent. If we become
            aware that we have unknowingly collected such information, we will
            make reasonable efforts to delete it from our records.
          </p>
          <p>
            We strongly recommend that minors 13 years of age or older ask their
            parents for permission before sending any personal information over
            the internet, and we encourage parents to teach their children about
            safe internet use practices.
          </p>
        </PolicySection>

        <!-- ── Security ──────────────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="security"
          eyebrow="Protection"
          title="Security of Your Information"
        >
          <p>
            Hooligans Eyewear Inc maintains reasonable safeguards to protect
            personal information against loss, unauthorized use, disclosure, or
            destruction — and requires third parties providing services on our
            behalf to do the same. However, no electronic data transmission or
            storage can be guaranteed to be 100% secure.
          </p>

          <CalloutBox>
            <strong>Please note:</strong> We cannot ensure or warrant the
            security of any information you transmit to us. Transmitting
            personal information is done at your own risk.
          </CalloutBox>
        </PolicySection>

        <!-- ── Other Sites ───────────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="other-sites"
          eyebrow="External Links"
          title="Other Sites"
        >
          <p>
            Our sites may contain links to third-party sites not owned or
            operated by Hooligans Eyewear Inc, including links from advertisers,
            sponsors, and partners. We do not control, recommend, or endorse
            these sites and are not responsible for their content, products,
            services, or privacy policies or practices.
          </p>
          <p>
            These other sites may send their own cookies or tracking
            technologies to your device, independently collect data, or solicit
            personal information. You should independently assess the
            authenticity of any site that claims to be one of our sites.
          </p>
          <p>
            Our sites may also make available chat rooms, forums, message
            boards, and news groups. Any information you disclose in these areas
            becomes public information and is not subject to the provisions of
            this Privacy Policy.
          </p>
        </PolicySection>

        <!-- ── Changes & Updates ─────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="changes"
          eyebrow="Policy Updates"
          title="Changes &amp; Updates"
        >
          <p>
            We may change information on our sites and/or this Privacy Policy at
            any time without prior notice. Changes will be effective immediately
            upon posting of the revised policy. Hooligans Eyewear Inc will use
            your personal information in a manner consistent with the Privacy
            Policy in effect at the time you submitted the information, unless
            you consent to the new or revised policy.
          </p>
          <p>
            We encourage you to periodically review this page for the latest
            information on our privacy practices.
          </p>
        </PolicySection>

        <!-- Contact CTA -->
        <ContactCta
          class="reveal"
          heading="Questions about this policy?"
          description="We're happy to clarify how your information is collected and used."
          button-text="Contact us"
          href="mailto:support@hooliganseyewear.com"
        />
      </div>
    </div>
  </div>
</template>

<script>
import "./privacy_policy.css";

import DeliveryHero from "~/components/delivery/DeliveryHero.vue";
import DeliverySidebar from "~/components/delivery/DeliverySidebar.vue";
import PolicySection from "~/components/delivery/PolicySection.vue";
import ContactCta from "~/components/delivery/ContactCta.vue";
import CalloutBox from "~/components/ui/CalloutBox.vue";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

// The one place for the policy's effective date; release prep sets it.
const PRIVACY_POLICY_EFFECTIVE_DATE = "October 6, 2026";

export default {
  name: "PrivacyPolicyPage",
  setup() {
    return useStaticPageSeo({
      title: "Privacy Policy | Hooligans Eyewear",
      heading: "Privacy Policy",
      description:
        "Learn how Hooligans Eyewear collects, uses, protects, and shares personal information when you visit our website or place an order.",
    });
  },
  components: {
    DeliveryHero,
    DeliverySidebar,
    PolicySection,
    ContactCta,
    CalloutBox,
  },
  data() {
    return {
      effectiveDate: PRIVACY_POLICY_EFFECTIVE_DATE,
      activeId: "information-collected",
      sections: [
        { id: "information-collected", long: "Information Collected" },
        { id: "automatically-collected", long: "Automatically Collected" },
        { id: "product-analytics", long: "Product Analytics" },
        { id: "sharing", long: "Sharing Your Information" },
        { id: "advertising", long: "Advertising & Opt-Out" },
        { id: "children", long: "Children" },
        { id: "security", long: "Security" },
        { id: "other-sites", long: "Other Sites" },
        { id: "changes", long: "Changes & Updates" },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },
    _updateActive() {
      const els = this.sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x) => x.el);
      if (!els.length) return;

      let current = els[0].id;
      for (const { id, el } of els) {
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      if (current !== this.activeId) {
        this.activeId = current;
      }
    },
    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
    },
  },
};
</script>
